import { action, computed, observable, runInAction } from 'mobx'
import { isGoogleBusinessReauthError } from '../../lib/googleBusinessAuth'
import { ScoredCategory, transformBusinessProfileData } from '../../lib/googleBusinessScoring'
import { $RootStore } from '../../../src/stores/RootStore'

export default class GoogleBusinessProfileStore {
  store: $RootStore

  @observable accessor categories: ScoredCategory[] = []
  @observable accessor error: string | null = null
  @observable accessor hasFetched = false
  @observable accessor loading = false
  @observable accessor businessName: string | null = null
  @observable accessor fetchedAt: Date | null = null
  @observable accessor needsReauth = false

  constructor(store: $RootStore) {
    this.store = store
  }

  @action fetchBusinessProfileData = async ({ useRandomData = false, forceRefresh = false }: { useRandomData?: boolean, forceRefresh?: boolean } = {}) => {
    if (this.hasFetched && !forceRefresh && !useRandomData) {
      return
    }

    try {
      runInAction(() => {
        this.error = null
        this.needsReauth = false
        this.loading = true
      })

      const detailsResponse = await this.store.communication.requester.getGoogleBusinessDetails()

      runInAction(() => {
        const selectedLocationId = this.store.userStore.data.googleBusinessAuth?.selectedLocationId
        if (detailsResponse?.getGoogleBusinessDetails && selectedLocationId) {
          for (const account of detailsResponse.getGoogleBusinessDetails) {
            const location = account.locations.find(loc => loc.name === selectedLocationId)
            if (location) {
              this.businessName = location.title
              break
            }
          }
        }
      })

      if (!this.store.userStore.data.googleBusinessAuth?.selectedLocationId) {
        runInAction(() => {
          this.loading = false
        })
        return
      }

      const profileResponse = await this.store.communication.requester.getBusinessProfileData({ useRandomData })

      runInAction(() => {
        if (profileResponse?.getBusinessProfileData) {
          this.categories = transformBusinessProfileData(profileResponse.getBusinessProfileData)
          this.hasFetched = true
          this.fetchedAt = profileResponse.getBusinessProfileData.fetchedAt
            ? new Date(profileResponse.getBusinessProfileData.fetchedAt)
            : null
        } else {
          this.error = 'No business profile data available'
        }

        this.loading = false
      })
    } catch (err) {
      console.error('Error fetching business profile data:', err)
      runInAction(() => {
        this.needsReauth = isGoogleBusinessReauthError(this.store.communication.latestErrorCode)
        this.error = 'Failed to load business profile data'
        this.loading = false
      })
    }
  }

  @computed get isConnectedToGoogleBusiness() {
    return !!this.store.userStore.data.googleBusinessAuth
  }

  @computed get hasSelectedLocation() {
    return !!this.store.userStore.data.googleBusinessAuth?.selectedLocationId
  }

  @computed get isFullySetup() {
    return this.isConnectedToGoogleBusiness && this.hasSelectedLocation && !this.needsReauth
  }

  @action clearData = () => {
    this.categories = []
    this.error = null
    this.needsReauth = false
    this.hasFetched = false
    this.loading = false
    this.businessName = null
    this.fetchedAt = null
  }
}
